import { type Location } from '@koala/sdk';
import { CONVEYANCE_TYPES } from '@koala/sdk/v4';
import { LOCATION_PILL_COPY } from './constants';
import { useLocationPillLabel } from './use-location-pill-label';
import { styled } from '@/components/content/stitches';
import { ChevronIcon } from '@/components/uielements/icon/chevron';
import { MapPinIcon } from '@/components/uielements/icon/map-pin';

/** Stable hook for parents that lay the pill out (styled-components cannot target a Stitches component). */
export const LOCATION_PILL_CLASS = 'location-pill';

interface Props {
  location: Pick<Location, 'label' | 'cached_data'>;
  conveyanceType?: string | null;
  /** Opens the Select Location pop-up. Without it the pill is a static label (single-location brands). */
  onChange?: () => void;
  disabled?: boolean;
  /** Brand-accent pin, as in the Figma, for light backgrounds; otherwise it follows the text colour. */
  accentPin?: boolean;
}

/**
 * Shows which store the diner is ordering from: a map pin, "PICKUP FROM" or
 * "DELIVERED FROM", and "Brand · Location" (TEAM-13315). With `onChange` it is a
 * button that opens the Select Location pop-up.
 */
export function LocationPill({ location, conveyanceType, onChange, disabled, accentPin }: Props) {
  const label = useLocationPillLabel(location);
  const eyebrow =
    conveyanceType === CONVEYANCE_TYPES.DELIVERY
      ? LOCATION_PILL_COPY.deliveryEyebrow
      : LOCATION_PILL_COPY.pickupEyebrow;

  const content = (
    <>
      <Pin aria-hidden="true" accent={Boolean(accentPin)}>
        <MapPinIcon title="" width="20" height="20" />
      </Pin>
      <Text>
        <Eyebrow>{eyebrow}</Eyebrow>
        <Label title={label}>{label}</Label>
      </Text>
    </>
  );

  if (!onChange) {
    return (
      <Pill className={LOCATION_PILL_CLASS} data-testid="location-pill">
        {content}
      </Pill>
    );
  }

  return (
    <Pill
      as="button"
      type="button"
      className={LOCATION_PILL_CLASS}
      data-testid="location-pill"
      aria-label={LOCATION_PILL_COPY.changeLocationLabel(label)}
      aria-haspopup="dialog"
      interactive
      disabled={disabled}
      onClick={onChange}
    >
      {content}
      <Chevron aria-hidden="true">
        <ChevronIcon direction="down" />
      </Chevron>
    </Pill>
  );
}

const Pill = styled('div', {
  alignItems: 'center',
  backgroundColor: 'transparent',
  border: '1px solid $shade400',
  borderRadius: '$md',
  color: 'inherit',
  display: 'flex',
  font: 'inherit',
  gap: '$2',
  maxWidth: '100%',
  minWidth: 0,
  padding: '$1 $2',
  textAlign: 'left',

  variants: {
    interactive: {
      true: {
        cursor: 'pointer',

        '&:hover': {
          borderColor: '$shade600',
        },

        // Inset, so a parent that clips overflow (the header's Destination) cannot hide it.
        '&:focus-visible': {
          outline: '2px solid currentColor',
          outlineOffset: '-4px',
        },

        '&:disabled': {
          cursor: 'not-allowed',
          opacity: 0.6,
        },
      },
    },
  },
});

// On a brand-coloured header the accent can match the background, so by default the pin
// follows the text colour; on light backgrounds it takes the brand accent (Figma).
const Pin = styled('span', {
  color: 'currentColor',
  display: 'flex',
  flexShrink: 0,

  variants: {
    accent: {
      true: {
        color: '$accentPrimary',
      },
    },
  },
});

const Text = styled('span', {
  display: 'flex',
  flexDirection: 'column',
  lineHeight: 1.2,
  minWidth: 0,
});

const Eyebrow = styled('span', {
  fontSize: '10px',
  letterSpacing: '$wider',
  opacity: 0.7,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
});

const Label = styled('span', {
  fontSize: '15px',
  fontWeight: 700,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',

  // TEAM-16124: on desktop (>=1280px) allow the store name to wrap to 2 lines
  // before truncating, so long names like "The Tomato Shack - Conshohocken" are
  // not cut off in the menu header's constrained middle column.
  '@xl': {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    whiteSpace: 'normal',
    textOverflow: 'clip',
  },
});

const Chevron = styled('span', {
  display: 'flex',
  flexShrink: 0,
  marginLeft: 'auto',
});
